'use client';

import { Inbox } from 'lucide-react';
import React from 'react';

import { cn } from '../../utils/cn';

import type { HTMLAttributes, ReactNode } from 'react';

export interface UzEmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Icon shown above the title. Defaults to an inbox icon. Pass null to hide. */
  icon?: ReactNode | null;
  title: ReactNode;
  description?: ReactNode;
  /** Primary CTA. */
  action?: ReactNode;
  /** Optional secondary CTA next to the primary. */
  secondaryAction?: ReactNode;
  /** Visual density. Compact removes padding for inline use. */
  size?: 'default' | 'compact';
}

export const UzEmptyState = ({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'default',
  className,
  ...rest
}: UzEmptyStateProps): React.JSX.Element => {
  const resolvedIcon =
    icon === null ? null : (icon ?? <Inbox className="text-muted-foreground h-8 w-8" />);
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        size === 'default' && 'gap-3 py-12',
        size === 'compact' && 'gap-2 py-6',
        className,
      )}
      {...rest}
    >
      {resolvedIcon && <div className="mb-1">{resolvedIcon}</div>}
      <h3 className="font-heading text-base font-semibold">{title}</h3>
      {description && <p className="text-muted-foreground max-w-md text-sm">{description}</p>}
      {(action || secondaryAction) && (
        <div className="mt-2 flex items-center gap-2">
          {secondaryAction}
          {action}
        </div>
      )}
    </div>
  );
};
